import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import AceEditor from 'react-ace'
import 'ace-builds/src-noconflict/mode-json'
import 'ace-builds/src-noconflict/theme-xcode'
import SydButton from '../../../../../commonDesign/Button'
import ResizableHandle from '../../QueryTool/ResizablePanels'

const useStyles = makeStyles(theme => ({
  diffContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    backgroundColor: 'var(--surface-bg-color-d4)'
  },
  diffHeader: {
    padding: theme.spacing(1, 1.5),
    borderBottom: '1px solid var(--color-neutral-600)',
    backgroundColor: 'var(--surface-bg-color-d2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0
  },
  diffTitle: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    color: 'var(--text-color-main)'
  },
  diffActions: {
    display: 'flex',
    gap: theme.spacing(1)
  },
  diffBody: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
    minHeight: 0,
    position: 'relative'
  },
  diffPanel: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: 0
  },
  diffPanelHeader: {
    padding: theme.spacing(0.5, 1),
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    color: 'var(--text-color-main)',
    borderBottom: '1px solid var(--color-neutral-600)',
    backgroundColor: 'var(--surface-bg-color-d3)',
    flexShrink: 0
  },
  diffEditor: {
    flex: 1,
    minHeight: 0
  }
}))

const diffStyles = `
  .ace_editor .ace_marker-layer .diff-added {
    background-color: rgba(0, 200, 0, 0.2);
    position: absolute;
  }
  .ace_editor .ace_marker-layer .diff-removed {
    background-color: rgba(255, 0, 0, 0.2);
    position: absolute;
  }
  .ace_editor .ace_marker-layer .diff-modified {
    background-color: rgba(255, 200, 0, 0.2);
    position: absolute;
  }
`

if (typeof document !== 'undefined' && !document.getElementById('pipeline-diff-styles')) {
  const style = document.createElement('style')
  style.id = 'pipeline-diff-styles'
  style.textContent = diffStyles
  document.head.appendChild(style)
}

function computeDiff (oldText, newText) {
  const oldLines = oldText.split('\n')
  const newLines = newText.split('\n')

  const m = oldLines.length
  const n = newLines.length

  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0))
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldLines[i - 1] === newLines[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1])
      }
    }
  }

  const oldMarkers = []
  const newMarkers = []

  let i = m
  let j = n
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
      i--
      j--
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      newMarkers.push({ line: j - 1, type: 'added' })
      j--
    } else {
      oldMarkers.push({ line: i - 1, type: 'removed' })
      i--
    }
  }

  return { oldMarkers, newMarkers }
}

function PipelineDiffView ({ currentPipeline, proposedPipeline, onAccept, onReject }) {
  const classes = useStyles()
  const containerRef = useRef(null)
  const [leftWidthPx, setLeftWidthPx] = useState(400)
  const currentEditorRef = useRef(null)
  const proposedEditorRef = useRef(null)
  const currentMarkerIdsRef = useRef([])
  const proposedMarkerIdsRef = useRef([])

  useEffect(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth
      if (containerWidth > 0 && leftWidthPx === 400) {
        setLeftWidthPx(containerWidth * 0.5)
      }
    }
  }, [leftWidthPx])

  const diffResult = useMemo(
    () => computeDiff(currentPipeline || '[]', proposedPipeline || '[]'),
    [currentPipeline, proposedPipeline]
  )

  useEffect(() => {
    if (!currentEditorRef.current?.editor || !proposedEditorRef.current?.editor) return

    const currentSession = currentEditorRef.current.editor.getSession()
    const proposedSession = proposedEditorRef.current.editor.getSession()

    currentMarkerIdsRef.current.forEach(id => currentSession.removeMarker(id))
    proposedMarkerIdsRef.current.forEach(id => proposedSession.removeMarker(id))
    currentMarkerIdsRef.current = []
    proposedMarkerIdsRef.current = []

    currentSession.clearAnnotations()
    proposedSession.clearAnnotations()

    const { oldMarkers, newMarkers } = diffResult

    oldMarkers.forEach(({ line, type }) => {
      const Range = currentEditorRef.current.editor.getSelection().getRange().constructor
      const range = new Range(line, 0, line, Number.MAX_VALUE)
      const markerId = currentSession.addMarker(range, `diff-${type}`, 'fullLine', false)
      currentMarkerIdsRef.current.push(markerId)
    })

    newMarkers.forEach(({ line, type }) => {
      const Range = proposedEditorRef.current.editor.getSelection().getRange().constructor
      const range = new Range(line, 0, line, Number.MAX_VALUE)
      const markerId = proposedSession.addMarker(range, `diff-${type}`, 'fullLine', false)
      proposedMarkerIdsRef.current.push(markerId)
    })
  }, [diffResult])

  const handleResize = useCallback((delta) => {
    setLeftWidthPx(prev => {
      if (!containerRef.current) return prev
      const newWidth = prev + delta
      const containerWidth = containerRef.current.offsetWidth
      const minWidth = containerWidth * 0.2
      const maxWidth = containerWidth * 0.8
      return Math.max(minWidth, Math.min(maxWidth, newWidth))
    })
  }, [])

  return (
    <div className={classes.diffContainer}>
      <div className={classes.diffHeader}>
        <span className={classes.diffTitle}>Proposed Pipeline Changes</span>
        <div className={classes.diffActions}>
          <SydButton
            size='sm'
            variant='secondary'
            onClick={onReject}
            icon='close'
          >
            Reject
          </SydButton>
          <SydButton
            size='sm'
            variant='primary'
            onClick={onAccept}
            icon='check'
          >
            Accept
          </SydButton>
        </div>
      </div>
      <div ref={containerRef} className={classes.diffBody}>
        <div className={classes.diffPanel} style={{ width: `${leftWidthPx}px` }}>
          <div className={classes.diffPanelHeader}>Current</div>
          <div className={classes.diffEditor}>
            <AceEditor
              ref={currentEditorRef}
              height='100%'
              width='100%'
              mode='json'
              theme='xcode'
              name='current-editor'
              readOnly
              editorProps={{ $blockScroll: true }}
              value={currentPipeline || '[]'}
              tabSize={2}
              showGutter
              showFoldWidgets
            />
          </div>
        </div>
        <ResizableHandle direction='horizontal' onResize={handleResize} />
        <div className={classes.diffPanel} style={{ flex: 1 }}>
          <div className={classes.diffPanelHeader}>Proposed</div>
          <div className={classes.diffEditor}>
            <AceEditor
              ref={proposedEditorRef}
              height='100%'
              width='100%'
              mode='json'
              theme='xcode'
              name='proposed-editor'
              readOnly
              editorProps={{ $blockScroll: true }}
              value={proposedPipeline || '[]'}
              tabSize={2}
              showGutter
              showFoldWidgets
            />
          </div>
        </div>
      </div>
    </div>
  )
}

PipelineDiffView.propTypes = {
  currentPipeline: PropTypes.string.isRequired,
  proposedPipeline: PropTypes.string.isRequired,
  onAccept: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired
}

export default PipelineDiffView
